import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Mic, MicOff, Send, X, ImagePlus, Volume2, VolumeX, Download, Megaphone, Camera, Maximize2, Loader2 } from "lucide-react";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FullscreenImageViewer from "@/components/FullscreenImageViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "Quero pedir uma corrida",
  "Quantas moedas eu tenho?",
  "Quanto tenho na carteira?",
  "Ver meus ganhos",
  "Buscar trabalhos",
  "Fazer saque",
  "Crie um banner para meu anúncio",
];

const DRIVER_SUGGESTIONS = [
  "Ganhos de hoje",
  "Corridas pendentes",
  "Abrir modo motorista",
  "Fazer saque",
];

function absUrl(u) {
  if (!u) return u;
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
}

// Leaflet marker icons (data URIs so we don't need extra assets)
const driverIcon = L.divIcon({
  className: "assist-driver-icon",
  html: `<div style="background:#2563EB;border:2px solid white;border-radius:50%;width:24px;height:24px;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;font-size:14px;">🚗</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});
const targetIcon = L.divIcon({
  className: "assist-target-icon",
  html: `<div style="background:#DC2626;border:2px solid white;border-radius:50%;width:20px;height:20px;box-shadow:0 2px 6px rgba(0,0,0,.35);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

export const OPEN_ASSISTANT_EVENT = "pdv:open-assistant";
export const openAssistant = () => window.dispatchEvent(new CustomEvent(OPEN_ASSISTANT_EVENT));

export default function AssistantWidget({ user, showFab = true }) {
  const cacheKey = `pdv-assist-msgs::${user?.user_id || "anon"}`;
  const initialGreeting = user?.is_driver
    ? "E aí, motorista! 🚗 Posso te mostrar ganhos de hoje, corridas pendentes, abrir modo motorista ou fazer saque. É só falar."
    : "Olá! Sou o assistente do FUTURO PDV. Peça pra chamar carro, ver saldo, sacar, buscar trabalho — ou até criar um banner de anúncio. 🚀";
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener(OPEN_ASSISTANT_EVENT, h);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, h);
  }, []);
  const [messages, setMessages] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) return JSON.parse(cached);
      } catch (err) { /* ignore */ }
    }
    return [{ role: "assistant", text: initialGreeting }];
  });

  // If user's driver status flips while widget is loaded, refresh greeting if chat is empty-ish
  useEffect(() => {
    setMessages((m) => {
      if (m.length <= 1) return [{ role: "assistant", text: initialGreeting }];
      return m;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_driver, user?.user_id]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [ttsOn, setTtsOn] = useState(false);
  const [genImageMode, setGenImageMode] = useState(false);
  const [headline, setHeadline] = useState("");
  const [refPhotoUrl, setRefPhotoUrl] = useState(null); // reference photo URL for image-to-image
  const [refPhotoUploading, setRefPhotoUploading] = useState(false);
  const [fullscreenImg, setFullscreenImg] = useState(null); // {url}
  const [downloadingIdx, setDownloadingIdx] = useState(null);
  const downloadBanner = async (i, image) => {
    if (downloadingIdx !== null) return;
    setDownloadingIdx(i);
    try {
      const r2 = await axios.get(absUrl(image), { responseType: "blob" });
      const url = window.URL.createObjectURL(r2.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `banner-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      window.open(absUrl(image), "_blank");
    } finally {
      setDownloadingIdx(null);
    }
  };
  const fileInputRef = useRef(null);
  const recogRef = useRef(null);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(`assist_${user?.user_id || "anon"}_${Date.now()}`);
  const navigate = useNavigate();

  // Persist chat
  useEffect(() => {
    try { localStorage.setItem(cacheKey, JSON.stringify(messages.slice(-40))); } catch (err) { /* ignore */ }
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Setup SpeechRecognition (WebKit / native)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recog = new SpeechRecognition();
    recog.lang = "pt-BR";
    recog.interimResults = false;
    recog.maxAlternatives = 1;
    recog.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      setListening(false);
      // auto-send
      setTimeout(() => handleSend(transcript), 60);
    };
    recog.onerror = () => setListening(false);
    recog.onend = () => setListening(false);
    recogRef.current = recog;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const speak = (txt) => {
    if (!ttsOn || typeof window === "undefined" || !window.speechSynthesis || !txt) return;
    try {
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(txt);
      utter.lang = "pt-BR";
      utter.rate = 1.0;
      window.speechSynthesis.speak(utter);
    } catch (err) { /* ignore */ }
  };

  const toggleMic = () => {
    if (!recogRef.current) {
      toast.error("Seu navegador não suporta reconhecimento de voz");
      return;
    }
    if (listening) {
      recogRef.current.stop();
      setListening(false);
    } else {
      try {
        recogRef.current.start();
        setListening(true);
      } catch (err) {
        // already started
        setListening(false);
      }
    }
  };

  const applyAction = (action, payload) => {
    if (action === "navigate" && payload?.path) {
      const path = payload.path.startsWith("/") ? payload.path : `/${payload.path}`;
      navigate(path);
      // Auto-close after a short delay so the destination page is visible
      setTimeout(() => setOpen(false), 900);
    }
  };

  const generateImage = async (prompt) => {
    setSending(true);
    const usedRef = refPhotoUrl;
    setMessages((m) => [...m, { role: "user", text: `🎨 Criar banner: ${prompt}${headline.trim() ? ` — manchete: "${headline.trim()}"` : ""}`, refPhoto: usedRef }]);
    try {
      const r = await axios.post(`${API}/assistant/image`, {
        prompt,
        headline: headline.trim() || null,
        purpose: user?.is_admin ? "admin_banner" : "ad_banner",
        session_id: sessionIdRef.current,
        input_image_url: usedRef || null,
      });
      const reply = r.data.reply || "Pronto!";
      speak(reply);
      setMessages((m) => [
        ...m,
        { role: "assistant", text: reply, image: r.data.url || null, imagePrompt: prompt },
      ]);
    } catch (e) {
      const msg = e.response?.data?.detail || "Não consegui gerar a imagem agora.";
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    }
    setSending(false);
    setGenImageMode(false);
    setRefPhotoUrl(null);
  };

  const handleRefPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Envie uma imagem (JPG, PNG ou WebP)");
      return;
    }
    setRefPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd);
      setRefPhotoUrl(r.data.url);
      setGenImageMode(true);
      toast.success("Foto carregada! Descreva como transformar em banner.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no upload");
    }
    setRefPhotoUploading(false);
    // reset the input so picking the same file again fires onChange
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const createAdFromImage = async (msgIdx, imageUrl, imagePrompt) => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/assistant/create-ad`, {
        image_url: imageUrl,
        prompt: imagePrompt,
        session_id: sessionIdRef.current,
      });
      const ad = r.data.ad;
      const reply = user?.is_admin
        ? `Anúncio criado e publicado: "${ad.title}"`
        : `Anúncio "${ad.title}" enviado para moderação. Você pode acompanhá-lo em Anúncios.`;
      // mark original image message as consumed
      setMessages((m) => {
        const next = [...m];
        if (next[msgIdx]) next[msgIdx] = { ...next[msgIdx], adCreated: ad };
        return [...next, { role: "assistant", text: reply, action: "navigate", payload: { path: "/ads" } }];
      });
      speak(reply);
      // Auto-navigate after a short delay
      setTimeout(() => { navigate("/ads"); setOpen(false); }, 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar anúncio");
    }
    setSending(false);
  };

  const handleSend = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    setInput("");
    if (genImageMode) {
      await generateImage(text);
      return;
    }
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/assistant/chat`, {
        message: text,
        session_id: sessionIdRef.current,
      });
      const { reply, action, payload, data } = r.data;
      speak(reply);
      // If model decided to generate image, call the image endpoint
      if (action === "generate_image" && payload?.prompt) {
        setMessages((m) => [...m, { role: "assistant", text: reply }]);
        await generateImage(payload.prompt);
      } else {
        setMessages((m) => [...m, { role: "assistant", text: reply, data, action, payload }]);
        applyAction(action, payload);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Assistente indisponível no momento.";
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    }
    setSending(false);
  };

  const clearChat = () => {
    setMessages([{ role: "assistant", text: initialGreeting }]);
    try { localStorage.removeItem(cacheKey); } catch (err) { /* ignore */ }
    sessionIdRef.current = `assist_${user?.user_id || "anon"}_${Date.now()}`;
  };

  const handleSuggestion = (s) => {
    if (s.action === "navigate" && s.path) {
      navigate(s.path);
      setTimeout(() => setOpen(false), 300);
      return;
    }
    if (s.action === "info" && s.fetch) {
      // Trigger a lightweight follow-up chat that maps to that fetch
      handleSend(s.label);
    }
  };

  const renderData = (m) => {
    if (!m.data) return null;
    // Cancel-ride success surface with suggestion chips
    if (m.data.ride_id && Array.isArray(m.data.suggestions)) {
      return (
        <div className="mt-2 space-y-2" data-testid="assist-data-cancel-done">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">{m.data.label}</div>
          <div className="text-xs text-muted-foreground">ID: <code className="text-foreground">{m.data.ride_id}</code></div>
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="assist-cancel-suggestions">
            {m.data.suggestions.map((s, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestion(s)}
                data-testid={`assist-cancel-suggestion-${idx}`}
                className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    // Simple note-only responses (no rides / not applicable to caller)
    if (m.data.note && !Array.isArray(m.data.rides) && !m.data.driver_location && m.data.coins === undefined && m.data.value_brl === undefined && m.data.total === undefined) {
      return (
        <div className="mt-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-xs text-amber-800" data-testid="assist-data-note-only">
          {m.data.note}
        </div>
      );
    }
    if (m.data.coins !== undefined) {
      return (
        <div className="mt-2 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/40" data-testid="assist-data-coins">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-yellow-700">{m.data.label}</div>
          <div className="text-2xl font-bold text-yellow-700">{m.data.coins.toLocaleString("pt-BR")}</div>
        </div>
      );
    }
    if (m.data.value_brl !== undefined) {
      return (
        <div className="mt-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/40" data-testid="assist-data-brl">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">{m.data.label}</div>
          <div className="text-2xl font-bold text-emerald-700">
            R$ {Number(m.data.value_brl).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
          </div>
          {m.data.rides_count != null && <div className="text-[11px] text-muted-foreground mt-0.5">{m.data.rides_count} corridas concluídas</div>}
        </div>
      );
    }
    if (m.data.total !== undefined) {
      return (
        <div className="mt-2 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/40" data-testid="assist-data-referrals">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">{m.data.label}</div>
          <div className="text-2xl font-bold text-emerald-700">{m.data.total}</div>
        </div>
      );
    }
    if (m.data.driver_location) {
      const dl = m.data.driver_location;
      const hasBothPoints = dl.lat != null && dl.lng != null && dl.target_lat != null && dl.target_lng != null;
      const center = hasBothPoints
        ? [(Number(dl.lat) + Number(dl.target_lat)) / 2, (Number(dl.lng) + Number(dl.target_lng)) / 2]
        : (dl.lat != null && dl.lng != null ? [Number(dl.lat), Number(dl.lng)] : null);
      return (
        <div className="mt-2 p-3 rounded-xl bg-blue-500/10 border border-blue-500/40" data-testid="assist-data-driver-location">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-700">{m.data.label}</div>
          <div className="flex items-center gap-2 mt-1">
            {dl.picture && <img src={dl.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{dl.name || "Motorista"}</div>
              <div className="text-[10px] text-muted-foreground">{dl.vehicle_type === "moto" ? "🏍" : "🚗"} {dl.plate || ""}</div>
            </div>
          </div>
          {center && (
            <div className="mt-2 rounded-lg overflow-hidden border border-blue-500/30 h-40" data-testid="assist-driver-map">
              <MapContainer center={center} zoom={hasBothPoints ? 13 : 15} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }} attributionControl={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[Number(dl.lat), Number(dl.lng)]} icon={driverIcon} />
                {hasBothPoints && (
                  <>
                    <Marker position={[Number(dl.target_lat), Number(dl.target_lng)]} icon={targetIcon} />
                    <Polyline positions={[[Number(dl.lat), Number(dl.lng)], [Number(dl.target_lat), Number(dl.target_lng)]]} pathOptions={{ color: "#2563EB", weight: 3, dashArray: "6 4" }} />
                  </>
                )}
              </MapContainer>
            </div>
          )}
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 rounded bg-background/60">
              <div className="text-[9px] uppercase text-muted-foreground">Distância</div>
              <div className="font-bold">{dl.distance_km != null ? `${dl.distance_km} km` : "—"}</div>
            </div>
            <div className="p-2 rounded bg-background/60">
              <div className="text-[9px] uppercase text-muted-foreground">ETA</div>
              <div className="font-bold">{dl.eta_min != null ? `~${dl.eta_min} min` : "—"}</div>
            </div>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1.5">Rumo: {dl.target}</div>
        </div>
      );
    }
    if (Array.isArray(m.data.rides)) {
      const isPending = m.data.label && m.data.label.toLowerCase().includes("pendentes");
      return (
        <div className="mt-2 space-y-1" data-testid={isPending ? "assist-data-pending" : "assist-data-rides"}>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{m.data.label}</div>
          {m.data.note && (
            <div className="text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded p-2" data-testid="assist-data-note">
              {m.data.note}
            </div>
          )}
          {!m.data.note && m.data.rides.length === 0 && <div className="text-xs text-muted-foreground italic">Nenhuma corrida no momento.</div>}
          {m.data.rides.map((r) => (
            <div key={r.ride_id} className="text-xs p-2 rounded bg-muted">
              {isPending ? (
                <>
                  <div className="font-semibold">🚗 R$ {Number(r.fare_estimate || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} · {r.vehicle_type || "carro"}</div>
                  <div className="text-muted-foreground truncate">{r.pickup_address} → {r.dest_address}</div>
                </>
              ) : (
                <>
                  <div className="font-semibold">{r.status === "completed" ? "✅" : "❌"} R$ {Number(r.fare_final || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-muted-foreground truncate">{r.pickup_address} → {r.dest_address}</div>
                </>
              )}
            </div>
          ))}
          {isPending && m.data.rides.length > 0 && (
            <Button
              size="sm"
              onClick={() => { navigate("/driver"); setTimeout(() => setOpen(false), 400); }}
              data-testid="assist-open-driver-btn"
              className="w-full rounded-full mt-1 h-8 text-xs"
            >
              Abrir modo motorista
            </Button>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Floating button */}
      {showFab && (
      <button
        onClick={() => setOpen(true)}
        data-testid="assistant-open-btn"
        className="fixed bottom-40 right-4 lg:bottom-24 lg:right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-primary to-blue-600 text-primary-foreground shadow-2xl shadow-primary/40 flex items-center justify-center hover:scale-105 transition-transform"
        title="Assistente IA"
      >
        <Sparkles className="w-6 h-6" />
      </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          data-testid="assistant-dialog"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md h-[85vh] sm:h-[600px] bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-bold text-sm tracking-tight">Assistente IA</div>
                  <div className="text-[10px] text-muted-foreground">FUTURO PDV · Gemini 3</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setTtsOn((v) => !v)}
                  data-testid="assistant-tts-toggle"
                  className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center"
                  title={ttsOn ? "Silenciar voz" : "Ativar voz"}
                >
                  {ttsOn ? <Volume2 className="w-4 h-4 text-primary" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
                </button>
                <button
                  onClick={clearChat}
                  data-testid="assistant-clear-btn"
                  className="text-[10px] px-2 py-1 rounded hover:bg-muted text-muted-foreground"
                  title="Limpar conversa"
                >
                  Limpar
                </button>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="assistant-close-btn"
                  className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2" ref={scrollRef} data-testid="assistant-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>
                    <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                    {m.refPhoto && (
                      <img
                        src={absUrl(m.refPhoto)}
                        alt="Foto de referência"
                        onClick={() => setFullscreenImg({ url: m.refPhoto })}
                        className="mt-1 rounded-lg max-w-[120px] cursor-zoom-in border border-white/20"
                        data-testid={`assist-msg-ref-${i}`}
                      />
                    )}
                    {m.image && (
                      <div className="mt-2 space-y-1.5">
                        <button
                          type="button"
                          onClick={() => setFullscreenImg({ url: m.image })}
                          className="block w-full rounded-lg overflow-hidden relative group cursor-zoom-in"
                          data-testid={`assist-image-open-${i}`}
                        >
                          <img src={absUrl(m.image)} alt="Banner gerado" className="w-full" data-testid={`assist-image-${i}`} />
                          <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Maximize2 className="w-3.5 h-3.5" />
                          </span>
                        </button>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => downloadBanner(i, m.image)}
                            disabled={downloadingIdx !== null}
                            aria-busy={downloadingIdx === i}
                            data-testid={`assist-image-download-${i}`}
                            className="text-xs inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-60 disabled:cursor-wait disabled:no-underline"
                          >
                            {downloadingIdx === i
                              ? <><Loader2 className="w-3 h-3 animate-spin" /> Baixando...</>
                              : <><Download className="w-3 h-3" /> Baixar em alta qualidade</>}
                          </button>
                          {m.imagePrompt && !m.adCreated && (
                            <Button
                              size="sm"
                              onClick={() => createAdFromImage(i, m.image, m.imagePrompt)}
                              disabled={sending}
                              data-testid={`assist-create-ad-${i}`}
                              className="h-7 rounded-full gap-1 text-xs"
                            >
                              <Megaphone className="w-3 h-3" /> Criar anúncio
                            </Button>
                          )}
                          {m.adCreated && (
                            <span className="text-[10px] text-emerald-600 font-semibold" data-testid={`assist-ad-created-${i}`}>
                              ✓ Anúncio criado
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {renderData(m)}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="assistant-typing">
                  <div className="bg-muted rounded-2xl px-3 py-2 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" />
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Suggestions */}
            {messages.length <= 1 && (
              <div className="px-3 pb-2 flex gap-1.5 flex-wrap" data-testid="assistant-suggestions">
                {(user?.is_driver ? DRIVER_SUGGESTIONS : SUGGESTIONS).slice(0, 4).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSend(s)}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input row */}
            <div className="p-3 border-t border-border">
              {genImageMode && (
                <div className="mb-2 space-y-1.5" data-testid="assistant-image-mode">
                  <div className="flex items-center gap-2 text-xs text-primary">
                    <ImagePlus className="w-3 h-3" />
                    Modo criação de imagem — descreva o banner
                    <button className="ml-auto text-muted-foreground" onClick={() => { setGenImageMode(false); setRefPhotoUrl(null); setHeadline(""); }}>cancelar</button>
                  </div>
                  <input
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value.slice(0, 60))}
                    maxLength={60}
                    placeholder="Manchete no banner (opcional) — ex.: Promoção de Verão 50% OFF"
                    data-testid="assistant-headline-input"
                    className="w-full h-8 rounded-full border border-border bg-background px-3 text-xs outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              )}
              {refPhotoUrl && (
                <div className="mb-2 flex items-center gap-2 p-2 rounded-lg border border-primary/40 bg-primary/5" data-testid="assistant-ref-preview">
                  <img src={absUrl(refPhotoUrl)} alt="Referência" className="w-12 h-12 rounded object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">Foto de referência</div>
                    <div className="text-[10px] text-muted-foreground truncate">A IA vai usar esta foto como base do banner.</div>
                  </div>
                  <button
                    onClick={() => setRefPhotoUrl(null)}
                    data-testid="assistant-ref-remove"
                    className="w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black"
                    aria-label="Remover"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleRefPhotoPick}
                className="hidden"
                data-testid="assistant-ref-file-input"
              />
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setGenImageMode((v) => !v)}
                  data-testid="assistant-image-mode-btn"
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${genImageMode ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  title="Criar banner com IA"
                >
                  <ImagePlus className="w-5 h-5" />
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={refPhotoUploading}
                  data-testid="assistant-ref-upload-btn"
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${refPhotoUrl ? "bg-emerald-500 text-white" : "hover:bg-muted"} ${refPhotoUploading ? "opacity-60" : ""}`}
                  title="Enviar foto para usar no banner"
                >
                  <Camera className="w-5 h-5" />
                </button>
                <button
                  onClick={toggleMic}
                  data-testid="assistant-mic-btn"
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${listening ? "bg-red-500 text-white animate-pulse" : "hover:bg-muted"}`}
                  title="Falar comando"
                >
                  {listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
                <Input
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleSend())}
                  placeholder={listening ? "Ouvindo..." : (genImageMode ? (refPhotoUrl ? "Descreva como transformar sua foto..." : "Descreva o banner...") : "Digite ou fale...")}
                  className="flex-1 rounded-full"
                  disabled={sending}
                />
                <Button
                  size="icon"
                  onClick={() => handleSend()}
                  disabled={sending || !input.trim()}
                  data-testid="assistant-send-btn"
                  className="rounded-full flex-shrink-0"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FullscreenImageViewer
        src={fullscreenImg ? absUrl(fullscreenImg.url) : null}
        onClose={() => setFullscreenImg(null)}
        filename={fullscreenImg?.filename}
      />
    </>
  );
}
